import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut, Compass, ShieldAlert } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { stripCredentialParams } from "../lib/stripCredentialParams";
import { useTour, TOURS } from "./Tour";

/**
 * Top-right nav widget.
 * Logged out → ghost "Sign in with Google" button.
 * Logged in  → avatar + name + dropdown (Sign out).
 *
 * 2026-07-27 CHANGE 3 — Validation queue entry removed. The pending-
 * count badge, the getValidationQueue() poller, and the /validation
 * link are gone; the standalone route was deleted alongside them.
 */
export default function UserMenu() {
    const { user, loading, signOut } = useAuth();
    const tour = useTour();
    const [open, setOpen] = useState(false);
    const [tourSubOpen, setTourSubOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (!menuRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (loading) {
        return (
            <div className="text-[12px] font-mono uppercase tracking-[0.15em] text-ink-soft/60">
                &middot;&middot;&middot;
            </div>
        );
    }

    if (!user) {
        return (
            <div className="inline-flex items-center gap-2">
                <Link
                    data-testid="signin-button"
                    to="/login"
                    onClick={() => {
                        try {
                            sessionStorage.setItem(
                                "scope:post_login",
                                stripCredentialParams(window.location.pathname + window.location.search)
                            );
                        } catch (_) { /* ignore */ }
                    }}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-ink/15 text-ink-soft hover:text-ink hover:border-ink/45 text-[12px] tracking-wide transition-colors"
                >
                    <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden>
                        <path fill="currentColor" d="M44.5 20H24v8.5h11.8C34.7 33 30 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l6-6C34.5 5.8 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11.1 0 20-8.9 20-20 0-1.4-.1-2.7-.5-4z" />
                    </svg>
                    Sign in
                </Link>
                <Link
                    data-testid="signup-button"
                    to="/register"
                    className="hidden sm:inline-flex items-center px-3.5 py-1.5 bg-ink text-paper hover:bg-amber-accent text-[12px] tracking-wide transition-colors"
                >
                    Sign up — free
                </Link>
            </div>
        );
    }

    const initials = (user.name || user.email || "?")
        .split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("");

    return (
        <div className="relative" ref={menuRef}>
            <button
                data-testid="user-menu-button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1 hover:bg-ink/5 transition-colors"
            >
                {user.picture ? (
                    <img
                        src={user.picture}
                        alt=""
                        className="w-7 h-7 rounded-full border border-ink/15"
                    />
                ) : (
                    <div className="w-7 h-7 rounded-full bg-ink text-paper flex items-center justify-center text-[10px] font-mono tracking-wide">
                        {initials || "U"}
                    </div>
                )}
                <span className="hidden md:inline text-[12px] tracking-wide text-ink-soft">
                    {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                </span>
                <ChevronDown size={12} className="text-ink-soft/70" />
            </button>
            {open && (
                <div
                    data-testid="user-menu-dropdown"
                    className="absolute right-0 mt-2 w-64 bg-paper border border-ink/15 shadow-lg z-50"
                >
                    <div className="px-4 py-3 border-b border-ink/10">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-ink-soft/70 mb-1">
                            Signed in as
                        </div>
                        <div className="text-ink text-[13px] font-serif">{user.name}</div>
                        <div className="text-ink-soft text-[11px] font-mono">{user.email}</div>
                    </div>
                    <Link
                        to="/analyses?mine=true"
                        data-testid="user-menu-my-analyses"
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2.5 text-[12px] text-ink hover:bg-ink/5 transition-colors"
                    >
                        Your analyses
                    </Link>
                    <Link
                        to="/account"
                        data-testid="user-menu-account"
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2.5 text-[12px] text-ink hover:bg-ink/5 transition-colors"
                    >
                        Account &amp; password
                    </Link>
                    <Link
                        to="/account/api-keys"
                        data-testid="user-menu-api-keys"
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2.5 text-[12px] text-ink hover:bg-ink/5 transition-colors"
                    >
                        API keys
                    </Link>

                    {user.is_admin && (
                        <Link
                            to="/admin"
                            data-testid="user-menu-admin"
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-between px-4 py-2.5 text-[12px] text-ink hover:bg-ink/5 transition-colors border-t border-ink/10"
                        >
                            <span className="inline-flex items-center gap-2">
                                <ShieldAlert size={12} className="text-amber-accent" />
                                Admin portal
                            </span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-amber-accent">Admin</span>
                        </Link>
                    )}

                    {/* ---- Restart tour entry (Phase 9 wire-up) ---- */}
                    <div className="border-t border-ink/10">
                        <button
                            type="button"
                            data-testid="user-menu-restart-tour"
                            onClick={() => setTourSubOpen((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-[12px] text-ink hover:bg-ink/5 transition-colors"
                        >
                            <span className="inline-flex items-center gap-2">
                                <Compass size={12} className="text-amber-accent" />
                                Restart tour
                            </span>
                            <ChevronDown
                                size={11}
                                className={`text-ink-soft/70 transition-transform ${tourSubOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {tourSubOpen && (
                            <div data-testid="user-menu-tour-submenu" className="bg-paper-soft/50">
                                {Object.values(TOURS).map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        data-testid={`user-menu-tour-${t.id}`}
                                        onClick={() => {
                                            setOpen(false);
                                            setTourSubOpen(false);
                                            tour?.start?.(t.id);
                                        }}
                                        className="w-full flex items-center justify-between px-6 py-2 text-left text-[11.5px] text-ink-soft hover:text-ink hover:bg-ink/5 transition-colors"
                                    >
                                        <span>{t.name}</span>
                                        <span className="font-mono text-[9px] text-ink-soft/60 uppercase tracking-[0.14em]">
                                            {t.durationLabel}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        data-testid="user-menu-signout"
                        onClick={async () => { setOpen(false); await signOut(); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 border-t border-ink/10 text-left text-[12px] text-ink-soft hover:text-ink hover:bg-ink/5 transition-colors"
                    >
                        <LogOut size={12} />
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}
